import React from 'react';
import { Link } from 'react-router-dom';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { Notification } from '@helpers/Models';
import { Assets } from '@helpers/Constants/Assets';

interface NotificationPopupProps {
  notifications?: Notification[];
  onClose?: () => void;
}

export const NotificationPopupComponent: React.FC<NotificationPopupProps> = ({
  notifications = [],
  onClose,
}) => {
  console.log(
    'NotificationPopupComponent received notifications:',
    notifications,
  );

  // Function to get current user ID from localStorage
  const getCurrentUserId = (): number | null => {
    try {
      const userDetails = localStorage.getItem('userDetails');
      if (userDetails) {
        const user = JSON.parse(userDetails);
        return user.id || null;
      }
    } catch (error) {
      console.error('Error parsing userDetails from localStorage:', error);
    }
    return null;
  };

  // Function to check if notification is read by current user
  const isNotificationReadByUser = (notification: Notification): boolean => {
    console.log(`Checking notification ${notification.id}:`, {
      isReadByUser: notification.isReadByUser,
      isRead: notification.isRead,
      currentUserId: getCurrentUserId(),
    });

    // If isReadByUser property exists, use it (consistent with AllNotifications page)
    if (notification.isReadByUser !== undefined) {
      return notification.isReadByUser;
    }

    // Fallback to checking isRead array
    const currentUserId = getCurrentUserId();
    if (!currentUserId || !notification.isRead) {
      return false;
    }
    return notification.isRead.includes(currentUserId);
  };

  // Function to get icon based on action key or module
  const getNotificationIcon = (actionKey: string) => {
    const iconMap: { [key: string]: string } = {
      CUSTOMER_CREATE: Assets.CUSTOMER,
      ORDER_CREATE: Assets.ORDER_CREATE,
      SERVICE_START: Assets.SERVICE_PROCESSING,
      SERVICE_FINISH: Assets.SERVICE_PROCESSING,
      PRODUCT_START: Assets.PRODUCT_PROCESSING,
      PRODUCT_FINISH: Assets.PRODUCT_PROCESSING,
      GENERATE_INVOICE: Assets.INVOICE,
      PAID: Assets.INVOICE,
      PICKED_UP: Assets.PICKUP,
    };

    return iconMap[actionKey] || Assets.SERVICE_PROCESSING;
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return '1 day ago';
    } else {
      return `${diffInDays} days ago`;
    }
  };

  return (
    <div className="relative mt-2">
      <div className="absolute w-4 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-black -top-2 right-20"></div>
      <div className="flex flex-col w-1/2 overflow-y-auto px-3 rounded-lg shadow-lg p-2 z-50 fixed right-5 bg-white">
        <div className="flex flex-col border-b border-[#A0A0A0] space-y-2 pb-2">
          {notifications.length > 0 ? (
            notifications.slice(0, 4).map((notification) => (
              <div
                key={notification.id}
                className={`flex gap-x-2 p-2 ${
                  !isNotificationReadByUser(notification) ? 'bg-[#FFFBF3]' : ''
                }`}
              >
                <div className="flex justify-center items-center">
                  <img
                    src={getNotificationIcon(notification.actionKey)}
                    alt="Notification"
                    className="w-10 h-10"
                  />
                </div>
                <div className="flex w-9/12 items-center px-2 text-base font-semibold">
                  {notification.message}
                </div>
                <div className="text-[#A0A0A0] flex items-center justify-center">
                  <div className="text-xs">
                    {formatDate(notification.createdAt)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center p-4 text-gray-500">
              No notifications available
            </div>
          )}
        </div>

        <div className="flex justify-center items-center border-t border-[#A0A0A0] p-2 ">
          <Link
            to={FrontendRoutes.NOTIFICATION}
            className="text-primary underline text-center font-bold"
            onClick={() => {
              console.log('Navigating to:', FrontendRoutes.NOTIFICATION);
              onClose?.();
            }}
          >
            Show all
          </Link>
        </div>
      </div>
    </div>
  );
};
